import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Settings } from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { pickLang } from "@/services/notifications-api.service";
import {
  useNotificationMutations,
  useNotificationsList,
  useNotificationsStream,
  useUnreadNotifications,
} from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";

export default function NotificationsPage() {
  useNotificationsStream(true);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 20;
  const { data: unread = 0 } = useUnreadNotifications();
  const { data: items = [], isLoading, isFetching } = useNotificationsList({
    unreadOnly,
    limit,
    offset: page * limit,
  });
  const { markRead, markAllRead } = useNotificationMutations();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 space-y-4 p-4 md:p-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/oi" aria-label="Назад">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            Уведомления
            <SectionHelpTooltip label="Справка по ленте уведомлений">
              <p className="font-medium">Лента серверных уведомлений</p>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                <li>Фильтры: все / непрочитанные</li>
                <li>Клик по строке — отметить прочитанным</li>
                <li>«Прочитать все» снимает бейдж колокольчика</li>
                <li>Настройки каналов — иконка шестерёнки</li>
              </ul>
            </SectionHelpTooltip>
          </h1>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/notifications/settings">
                <Settings className="mr-1 h-4 w-4" />
                Настройки
              </Link>
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base">
              Лента {unread > 0 ? `(непрочитанных: ${unread})` : ""}
            </CardTitle>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={!unreadOnly ? "default" : "outline"}
                onClick={() => {
                  setUnreadOnly(false);
                  setPage(0);
                }}
              >
                Все
              </Button>
              <Button
                size="sm"
                variant={unreadOnly ? "default" : "outline"}
                onClick={() => {
                  setUnreadOnly(true);
                  setPage(0);
                }}
              >
                Непрочитанные
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={unread === 0 || markAllRead.isPending}
                onClick={() => markAllRead.mutate()}
              >
                Прочитать все
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(isLoading || isFetching) && items.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">Загрузка…</p>
            )}
            {!isLoading && items.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">Пусто</p>
            )}
            {items.map((item) => (
              <button
                key={item.notification_id}
                type="button"
                className={cn(
                  "w-full rounded-md border px-3 py-2 text-left hover:bg-muted/50",
                  !item.read_at && "border-primary/40 bg-muted/20",
                )}
                onClick={() => {
                  if (!item.read_at) markRead.mutate(item.notification_id);
                }}
              >
                <div className="text-sm font-medium">
                  {pickLang(item.title as Record<string, string>)}
                </div>
                <div className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                  {pickLang(item.body as Record<string, string>)}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  {new Date(item.created_at).toLocaleString("ru-RU")} · {item.scope}
                </div>
              </button>
            ))}
            <div className="flex justify-between pt-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Назад
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={items.length < limit}
                onClick={() => setPage((p) => p + 1)}
              >
                Далее
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
